import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Alert, ProgressBar, Table } from "react-bootstrap";
import { LoadingOutlined } from "@ant-design/icons";
import { useDropzone, type FileRejection } from "react-dropzone";
import { Link } from "react-router-dom";

import PageTitle from "@/common/PageTitle/PageTitle";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import UploaderDropboxIcon from "@/assets/images/uploader-dropbox-icon.svg";
import {
  useCanEditPage,
  useCanViewPage,
} from "@/contexts/SuperAdminIdentityContext";
import {
  useCreateUploadMutation,
  useReplaceSongAudioMutation,
  useListProcessingJobsQuery,
  useCountProcessingJobsQuery,
  isProcessingApiNotConfigured,
  extractDuplicateUploadDetails,
  extractProcessingErrorMessage,
  extractReplacementAlreadyInProgress,
} from "@/features/processing/processingApi";
import { DuplicateUploadModal } from "@/features/processing/DuplicateUploadModal";
import { UploadConfirmModal } from "@/features/processing/UploadConfirmModal";
import {
  ReplaceSongAudioConfirmModal,
  type ReplaceSongAudioTarget,
} from "@/features/processing/ReplaceSongAudioConfirmModal";
import {
  renderChecksumBadge,
  renderSongTypeBadge,
  renderStateBadge,
  formatDateTime,
} from "@/features/processing/processingDisplay";
import {
  MUSIC_UPLOAD_CONTENT_TYPE,
  MUSIC_UPLOAD_MAX_BYTES,
  MUSIC_UPLOAD_MIN_BYTES,
  computeFileSha256Hex,
  putFileToPresignedUrl,
  validateMp3File,
} from "@/features/processing/processingUploadService";
import { initAndPutReplaceSongAudio } from "@/features/processing/replaceSongAudioClient";
import { resolveKkOriginalUploadHints } from "@/features/processing/kkOriginalUploadMetadata";
import {
  DEFAULT_UPLOAD_CONCURRENCY,
  DEFAULT_UPLOAD_MAX_ATTEMPTS,
  formatUploadExhaustedMessage,
  formatUploadTransientMessage,
  isTransientUploadError,
  newUploadIdempotencyKey,
  runPool,
  uploadRetryDelayMs,
} from "@/features/processing/uploadConcurrency";
import type { MediaSource } from "@/features/processing/mediaSource";
import {
  uploadGenreFields,
  type UploadGenreSelection,
} from "@/features/processing/uploadGenreFields";
import {
  useListGenresQuery,
  activeGenreOptions,
} from "@/features/genres/genresApi";
import {
  UPLOADER_COMPLETE_CLEAR_DELAY_MS,
  UPLOADER_INTAKE_IN_FLIGHT_STATES,
  jobBelongsInUploaderIntakeQueue,
} from "@/features/processing/processingTypes";
import type {
  DuplicateUploadDetails,
  ProcessingJobRow,
  SongType,
} from "@/features/processing/processingTypes";
import { PROCESSING_API_UNAVAILABLE_MESSAGE } from "@/features/processing/processingMessages";
import {
  applyUploadSessionOutcome,
  buildUploadSessionReceipt,
  formatUploadSessionCompletedAt,
  formatUploadSessionCounts,
  formatUploadSessionHeadline,
  formatUploadSessionLead,
  uploadSessionBatchComplete,
  uploadSessionNeedsAttention,
  type UploadSessionReceipt,
} from "@/features/processing/uploadSessionReceipt";
import {
  activePutVisualActiveCopy,
  activePutVisualSafeCopy,
  countActiveByteTransfers,
  hasActiveByteTransfer,
} from "@/features/processing/activeByteTransferGuard";
import { useActiveByteTransferGuard } from "@/features/processing/useActiveByteTransferGuard";
import { useActivePutVisualNotice } from "@/features/processing/useActivePutVisualNotice";
import styles from "./uploader.module.scss";

const PAGE_ID = "uploader";
/** Intake queue page size (not Song Processing's dense Active warehouse). */
const PAGE_SIZE = 50;

interface ActiveUploadRow {
  localId: string;
  fileName: string;
  songType: SongType;
  jobId?: string;
  phase:
    | "queued"
    | "initiating"
    | "retrying"
    | "uploading"
    | "processing"
    | "complete"
    | "failed";
  progress: number;
  error?: string;
  attempt?: number;
}

/**
 * DRA-2/DRA-3 — duplicate gate rejection parked while the operator chooses.
 * Retains the File so Replace Song Audio can reuse the same bytes.
 */
interface PendingDuplicateUpload {
  file: File;
  fileName: string;
  details: DuplicateUploadDetails;
}

/** In-session batch tracker for the upload receipt (not durable). */
interface UploadBatchTracker {
  batchId: string;
  localIds: Set<string>;
  /** localId → jobId once known */
  jobByLocalId: Map<string, string>;
  succeeded: Set<string>;
  failed: Set<string>;
  needsReview: Set<string>;
  finalized: boolean;
}

const UploaderPage: React.FC = () => {
  const canEdit = useCanEditPage(PAGE_ID);
  const canView = useCanViewPage(PAGE_ID);

  /** Parked after drop/select until Confirm upload modal completes. */
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  /** Modal draft — never default Song type; Source empty until chosen. */
  const [modalSongType, setModalSongType] = useState<SongType | null>(null);
  const [modalMediaSource, setModalMediaSource] = useState<MediaSource | "">("");
  /** Optional Genre/Subgenre — applies to the ENTIRE upload batch. */
  const [modalGenreId, setModalGenreId] = useState<string | "">("");
  const [modalSubgenreId, setModalSubgenreId] = useState<string | "">("");
  const [activeUploads, setActiveUploads] = useState<ActiveUploadRow[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDuplicate, setPendingDuplicate] =
    useState<PendingDuplicateUpload | null>(null);
  /** Session receipt — dismissed or replaced when a new batch starts. */
  const [sessionReceipt, setSessionReceipt] =
    useState<UploadSessionReceipt | null>(null);
  const intakeSectionRef = useRef<HTMLElement | null>(null);
  const batchTrackerRef = useRef<UploadBatchTracker | null>(null);

  const [createUpload] = useCreateUploadMutation();
  const [replaceSongAudio] = useReplaceSongAudioMutation();
  const [replaceTarget, setReplaceTarget] = useState<ReplaceSongAudioTarget | null>(
    null,
  );
  const [replaceError, setReplaceError] = useState("");
  const [replaceErrorOpenPath, setReplaceErrorOpenPath] = useState<string | null>(
    null,
  );
  const [replaceBusy, setReplaceBusy] = useState(false);

  const hasActiveSession = activeUploads.some(
    (row) => row.phase !== "complete" && row.phase !== "failed",
  );
  // Unsafe window = browser PUT in flight only (not initiating / processing / handed off).
  const activeByteTransfer = hasActiveByteTransfer(activeUploads);
  const activePutCount = countActiveByteTransfers(activeUploads);
  // Batch-aware plural: count in-session rows so 6-file batches stay "files"/"songs".
  const episodeFileCountHint = Math.max(
    activePutCount,
    activeUploads.filter((row) =>
      ["queued", "initiating", "retrying", "uploading", "processing"].includes(
        row.phase,
      ),
    ).length,
  );
  useActiveByteTransferGuard(activeByteTransfer, activePutCount);
  // Visual notice may persist briefly after PUT with SAFE copy (readable min).
  // Guards release immediately when activeByteTransfer goes false — visual ≠ safety.
  const putVisualNotice = useActivePutVisualNotice(
    activeByteTransfer,
    episodeFileCountHint,
  );
  const pagePutBannerCopy =
    putVisualNotice.kind === "active"
      ? activePutVisualActiveCopy(putVisualNotice.episodeFileCount)
      : putVisualNotice.kind === "safe"
        ? activePutVisualSafeCopy(putVisualNotice.episodeFileCount)
        : null;

  // Poll while session uploads are active or intake/handoff jobs are moving.
  const [hasIntakeInFlight, setHasIntakeInFlight] = useState(false);
  // Cursor paging for intakeQueue (dense ?page= seek is disabled server-side).
  const [listPage, setListPage] = useState(1);
  const [pageToken, setPageToken] = useState<string | undefined>(undefined);
  const [prevPageTokens, setPrevPageTokens] = useState<(string | undefined)[]>(
    [],
  );
  const seenIntakeJobIdsRef = useRef<Set<string>>(new Set());
  const completeClearScheduledRef = useRef<Set<string>>(new Set());

  const intakeListArgs = useMemo(
    () => ({
      intakeQueue: true as const,
      limit: PAGE_SIZE,
      pageSize: PAGE_SIZE,
      ...(pageToken ? { pageToken } : {}),
    }),
    [pageToken],
  );

  const {
    data: jobsData,
    error: jobsError,
    isFetching: jobsFetching,
  } = useListProcessingJobsQuery(intakeListArgs, {
    skip: !canView,
    refetchOnMountOrArgChange: true,
    pollingInterval:
      canView && (hasActiveSession || hasIntakeInFlight) ? 5000 : 0,
  });

  const countQuery = useCountProcessingJobsQuery(
    { intakeQueue: true },
    { skip: !canView },
  );

  // Active taxonomy for the optional Genre/Subgenre pickers (counts=false —
  // picker dropdowns never need songCount).
  const { data: genresData } = useListGenresQuery(
    { status: "active", counts: false },
    { skip: !canEdit },
  );
  const uploadGenreOptions = useMemo(
    () => activeGenreOptions(genresData?.genres),
    [genresData?.genres],
  );

  const apiNotConfigured = isProcessingApiNotConfigured(jobsError);

  // Server intakeQueue is authoritative once deployed; client filter is
  // deploy-order safety so Approved rows never reappear against an older API.
  const recentJobs = useMemo(
    () => (jobsData?.jobs ?? []).filter(jobBelongsInUploaderIntakeQueue),
    [jobsData?.jobs],
  );

  const matchingTotal =
    countQuery.data != null && countQuery.data.count >= 0
      ? countQuery.data.count
      : null;
  const matchingExact = countQuery.data?.exact ?? true;
  const totalPages =
    matchingTotal != null && matchingExact
      ? Math.max(1, Math.ceil(matchingTotal / PAGE_SIZE))
      : null;

  useEffect(() => {
    setHasIntakeInFlight(
      recentJobs.some((j) => UPLOADER_INTAKE_IN_FLIGHT_STATES.has(j.state)),
    );
    for (const job of recentJobs) {
      seenIntakeJobIdsRef.current.add(job.jobId);
    }
  }, [recentJobs]);

  // Dropzone opens when the operator can upload; Song type + Source are
  // collected intentionally in the post-drop Confirm upload modal.
  const uploadBlockedReason = useMemo((): string | null => {
    if (apiNotConfigured) {
      return PROCESSING_API_UNAVAILABLE_MESSAGE;
    }
    if (!canEdit) {
      return "You do not have permission to upload songs.";
    }
    return null;
  }, [apiNotConfigured, canEdit]);

  // Prevent the browser from navigating to file:///… when a file is dropped
  // outside an active dropzone handler (e.g. while dropzone is disabled).
  useEffect(() => {
    const preventBrowserFileNavigation = (event: DragEvent) => {
      event.preventDefault();
    };

    window.addEventListener("dragover", preventBrowserFileNavigation);
    window.addEventListener("drop", preventBrowserFileNavigation);

    return () => {
      window.removeEventListener("dragover", preventBrowserFileNavigation);
      window.removeEventListener("drop", preventBrowserFileNavigation);
    };
  }, []);

  const tryFinalizeSessionReceipt = useCallback(() => {
    const tracker = batchTrackerRef.current;
    if (!tracker || tracker.finalized) return;
    // Entire batch diverted (e.g. all duplicates parked for a decision).
    if (tracker.localIds.size === 0) {
      batchTrackerRef.current = null;
      return;
    }
    if (
      !uploadSessionBatchComplete(tracker.localIds, {
        succeeded: tracker.succeeded,
        failed: tracker.failed,
        needsReview: tracker.needsReview,
      })
    ) {
      return;
    }
    tracker.finalized = true;
    setSessionReceipt(
      buildUploadSessionReceipt({
        batchId: tracker.batchId,
        requested: tracker.localIds.size,
        succeeded: tracker.succeeded.size,
        failed: tracker.failed.size,
        needsReview: tracker.needsReview.size,
      }),
    );
  }, []);

  const recordBatchOutcome = useCallback(
    (localId: string, outcome: "succeeded" | "failed" | "needsReview") => {
      const tracker = batchTrackerRef.current;
      if (!tracker || tracker.finalized || !tracker.localIds.has(localId)) {
        return;
      }
      applyUploadSessionOutcome(tracker, localId, outcome);
      tryFinalizeSessionReceipt();
    },
    [tryFinalizeSessionReceipt],
  );

  const updateActiveUpload = useCallback(
    (localId: string, patch: Partial<ActiveUploadRow>) => {
      if (typeof patch.jobId === "string" && patch.jobId) {
        const tracker = batchTrackerRef.current;
        if (tracker && tracker.localIds.has(localId)) {
          tracker.jobByLocalId.set(localId, patch.jobId);
        }
      }
      if (patch.phase === "failed") {
        recordBatchOutcome(localId, "failed");
      }
      if (patch.phase === "complete") {
        recordBatchOutcome(localId, "succeeded");
      }
      setActiveUploads((prev) =>
        prev.map((row) => (row.localId === localId ? { ...row, ...patch } : row)),
      );
    },
    [recordBatchOutcome],
  );

  const uploadFile = useCallback(
    async (
      file: File,
      localId: string,
      opts: {
        songType: SongType;
        mediaSource: MediaSource;
        genre?: UploadGenreSelection;
      },
    ) => {
      const { songType, mediaSource, genre } = opts;
      let titleHint: string | undefined;
      let artistNameHint: string | undefined;
      if (songType === "kk-original") {
        const resolved = resolveKkOriginalUploadHints(file.name);
        if (!resolved.ok) {
          updateActiveUpload(localId, {
            phase: "failed",
            progress: 0,
            error: resolved.message,
          });
          return;
        }
        titleHint = resolved.title;
        artistNameHint = resolved.artistName;
      }

      // Stable across retries so a lost 200 cannot create a second job.
      const idempotencyKey = newUploadIdempotencyKey(file.name);
      const checksumHint = await computeFileSha256Hex(file);
      let lastError: unknown = null;

      for (let attempt = 1; attempt <= DEFAULT_UPLOAD_MAX_ATTEMPTS; attempt += 1) {
        updateActiveUpload(localId, {
          phase: attempt === 1 ? "initiating" : "retrying",
          progress: 0,
          attempt,
          error:
            attempt === 1
              ? undefined
              : formatUploadTransientMessage(attempt, DEFAULT_UPLOAD_MAX_ATTEMPTS),
        });

        try {
          const init = await createUpload({
            songType,
            originalFilename: file.name,
            sizeBytes: file.size,
            contentType: MUSIC_UPLOAD_CONTENT_TYPE,
            mediaSource,
            idempotencyKey,
            ...(songType === "kk-original"
              ? { title: titleHint, artistName: artistNameHint }
              : {}),
            ...(checksumHint ? { checksumSha256Hint: checksumHint } : {}),
            ...uploadGenreFields(genre),
          }).unwrap();

          updateActiveUpload(localId, {
            jobId: init.jobId,
            phase: "uploading",
            progress: 0,
            error: undefined,
            attempt,
          });

          await putFileToPresignedUrl(
            init.uploadUrl,
            file,
            init.requiredHeaders["Content-Type"],
            (pct) => updateActiveUpload(localId, { progress: pct }),
          );

          updateActiveUpload(localId, {
            phase: "processing",
            progress: 100,
            error: undefined,
          });
          return;
        } catch (err) {
          // DUP-ui — a duplicate 409 is a decision point, not a failure.
          // Exclude from the current session receipt (operator paused for a decision).
          const duplicate = extractDuplicateUploadDetails(err);
          if (duplicate) {
            const tracker = batchTrackerRef.current;
            if (tracker && !tracker.finalized && tracker.localIds.has(localId)) {
              tracker.localIds.delete(localId);
              tracker.jobByLocalId.delete(localId);
              tracker.succeeded.delete(localId);
              tracker.failed.delete(localId);
              tracker.needsReview.delete(localId);
              tryFinalizeSessionReceipt();
            }
            setActiveUploads((prev) => prev.filter((r) => r.localId !== localId));
            setPendingDuplicate({
              file,
              fileName: file.name,
              details: duplicate,
            });
            return;
          }

          lastError = err;
          const canRetry =
            attempt < DEFAULT_UPLOAD_MAX_ATTEMPTS && isTransientUploadError(err);
          if (!canRetry) {
            break;
          }

          updateActiveUpload(localId, {
            phase: "retrying",
            attempt,
            error: formatUploadTransientMessage(
              attempt + 1,
              DEFAULT_UPLOAD_MAX_ATTEMPTS,
            ),
          });
          await new Promise((r) => setTimeout(r, uploadRetryDelayMs(attempt)));
        }
      }

      const raw = extractProcessingErrorMessage(lastError) || "Upload failed";
      const message = isTransientUploadError(lastError)
        ? formatUploadExhaustedMessage(DEFAULT_UPLOAD_MAX_ATTEMPTS)
        : raw === "Service Unavailable"
          ? "Upload temporarily unavailable. Wait a moment, then try again."
          : raw;
      updateActiveUpload(localId, {
        phase: "failed",
        error: message,
      });
      setFormError(`${file.name}: ${message}`);
    },
    [createUpload, tryFinalizeSessionReceipt, updateActiveUpload],
  );

  const resetUploadConfirmModal = useCallback(() => {
    setPendingFiles(null);
    setModalSongType(null);
    setModalMediaSource("");
    setModalGenreId("");
    setModalSubgenreId("");
  }, []);

  const startUploadBatch = useCallback(
    (
      files: File[],
      songType: SongType,
      mediaSource: MediaSource,
      genre?: UploadGenreSelection,
    ) => {
      const batch: Array<{ file: File; localId: string }> = [];
      const stamp = Date.now();
      const queuedRows: ActiveUploadRow[] = [];

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const localId = `${stamp}-${i}-${file.name}`;
        queuedRows.push({
          localId,
          fileName: file.name,
          songType,
          phase: "queued",
          progress: 0,
        });
        batch.push({ file, localId });
      }

      if (queuedRows.length === 0) return;

      // New batch replaces any prior session receipt (in-memory only).
      setSessionReceipt(null);
      batchTrackerRef.current = {
        batchId: `upload-${stamp}`,
        localIds: new Set(batch.map((b) => b.localId)),
        jobByLocalId: new Map(),
        succeeded: new Set(),
        failed: new Set(),
        needsReview: new Set(),
        finalized: false,
      };

      setFormError(null);
      setListPage(1);
      setActiveUploads((prev) => [...queuedRows, ...prev]);

      void runPool(batch, DEFAULT_UPLOAD_CONCURRENCY, async ({ file, localId }) => {
        await uploadFile(file, localId, { songType, mediaSource, genre });
      });
    },
    [uploadFile],
  );

  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      if (uploadBlockedReason) {
        setFormError(uploadBlockedReason);
        return;
      }
      if (!canEdit) {
        setFormError("You do not have permission to upload songs.");
        return;
      }
      if (pendingFiles != null) {
        setFormError(
          "Finish confirming Song type and Source for the selected files, or cancel.",
        );
        return;
      }

      const stamp = Date.now();
      const failedRows: ActiveUploadRow[] = [];
      const validFiles: File[] = [];

      for (let i = 0; i < acceptedFiles.length; i += 1) {
        const file = acceptedFiles[i];
        const validationError = validateMp3File(file);
        if (validationError) {
          failedRows.push({
            localId: `${stamp}-invalid-${i}-${file.name}`,
            fileName: file.name,
            // Badge placeholder only — file never entered the confirm/upload path.
            songType: "kk-original",
            phase: "failed",
            progress: 0,
            error: validationError,
          });
          continue;
        }
        validFiles.push(file);
      }

      if (failedRows.length > 0) {
        setListPage(1);
        setActiveUploads((prev) => [...failedRows, ...prev]);
      }

      if (validFiles.length === 0) return;

      setFormError(null);
      setModalSongType(null);
      setModalMediaSource("");
      setModalGenreId("");
      setModalSubgenreId("");
      setPendingFiles(validFiles);
    },
    [canEdit, pendingFiles, uploadBlockedReason],
  );

  const handleConfirmUpload = useCallback(() => {
    if (!pendingFiles || pendingFiles.length === 0) return;
    if (modalSongType == null || !modalMediaSource) return;
    const files = pendingFiles;
    const songType = modalSongType;
    const mediaSource = modalMediaSource;
    // Commercial multi-file uploads never carry a batch-wide genre (owner
    // 2026-08-29): Genre/Subgenre is assigned per song by identification.
    // A single Commercial file may carry an optional operator override.
    const commercialBatch = songType === "commercial" && files.length > 1;
    const genre: UploadGenreSelection = commercialBatch
      ? { genreId: "", subgenreId: "" }
      : {
          genreId: modalGenreId,
          subgenreId: modalSubgenreId,
        };
    resetUploadConfirmModal();
    startUploadBatch(files, songType, mediaSource, genre);
  }, [
    modalGenreId,
    modalMediaSource,
    modalSongType,
    modalSubgenreId,
    pendingFiles,
    resetUploadConfirmModal,
    startUploadBatch,
  ]);

  const onDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      if (uploadBlockedReason) {
        setFormError(uploadBlockedReason);
        return;
      }
      const first = rejections[0];
      if (!first) return;

      const dropzoneError = first.errors[0]?.code;
      if (dropzoneError === "file-too-large") {
        setFormError(
          `File exceeds the ${Math.floor(MUSIC_UPLOAD_MAX_BYTES / (1024 * 1024))} MB limit.`,
        );
        return;
      }
      if (dropzoneError === "file-too-small") {
        setFormError(`File must be at least ${MUSIC_UPLOAD_MIN_BYTES} bytes.`);
        return;
      }
      if (dropzoneError === "file-invalid-type") {
        setFormError("Only MP3 files (audio/mpeg) are supported.");
        return;
      }

      setFormError(validateMp3File(first.file) || "File could not be accepted.");
    },
    [uploadBlockedReason],
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept: { "audio/mpeg": [".mp3"] },
    maxSize: MUSIC_UPLOAD_MAX_BYTES,
    minSize: MUSIC_UPLOAD_MIN_BYTES,
    multiple: true,
    noClick: Boolean(uploadBlockedReason),
    noKeyboard: Boolean(uploadBlockedReason),
    // Keep drag handlers active so drops are intercepted even when upload is blocked.
    disabled: false,
  });

  const mergedRecentRows = useMemo(() => {
    const byJobId = new Map<string, ProcessingJobRow>();
    for (const job of recentJobs) {
      byJobId.set(job.jobId, job);
    }

    const rows: Array<
      | { kind: "active"; row: ActiveUploadRow; job?: ProcessingJobRow }
      | { kind: "job"; job: ProcessingJobRow }
    > = [];

    // Active session rows stay visible on page 1 so operators see in-progress
    // uploads without leaving the first page of the dense index.
    if (listPage === 1) {
      for (const active of activeUploads) {
        const job = active.jobId ? byJobId.get(active.jobId) : undefined;
        rows.push({ kind: "active", row: active, job });
      }
    }

    for (const job of recentJobs) {
      if (activeUploads.some((a) => a.jobId === job.jobId)) continue;
      rows.push({ kind: "job", job });
    }

    return rows;
  }, [activeUploads, listPage, recentJobs]);

  const handlePrev = () => {
    if (prevPageTokens.length === 0) {
      setPageToken(undefined);
      setListPage(1);
      return;
    }
    const prior = prevPageTokens[prevPageTokens.length - 1];
    setPrevPageTokens((stack) => stack.slice(0, -1));
    setPageToken(prior);
    setListPage((p) => Math.max(1, p - 1));
  };

  const handleNext = () => {
    const nextToken = jobsData?.pageToken;
    if (!nextToken) return;
    setPrevPageTokens((stack) => [...stack, pageToken]);
    setPageToken(nextToken);
    setListPage((p) => p + 1);
  };

  const handlePageJump = (page: number) => {
    // Intake queue uses cursor tokens — only reset to page 1 is supported.
    if (page <= 1) {
      setPageToken(undefined);
      setPrevPageTokens([]);
      setListPage(1);
    }
  };

  const showPollingHint =
    hasActiveSession ||
    recentJobs.some((j) => UPLOADER_INTAKE_IN_FLIGHT_STATES.has(j.state));

  /** Songs still in intake / handoff (not past successful handoff). */
  const processingNowCount = useMemo(() => {
    const countedJobIds = new Set<string>();
    let count = 0;

    for (const row of activeUploads) {
      if (row.phase === "complete" || row.phase === "failed") continue;
      count += 1;
      if (row.jobId) countedJobIds.add(row.jobId);
    }

    for (const job of recentJobs) {
      if (countedJobIds.has(job.jobId)) continue;
      if (UPLOADER_INTAKE_IN_FLIGHT_STATES.has(job.state)) {
        count += 1;
      }
    }

    return count;
  }, [activeUploads, recentJobs]);

  useEffect(() => {
    setActiveUploads((prev) =>
      prev.map((row) => {
        if (!row.jobId || row.phase === "failed" || row.phase === "complete") {
          return row;
        }
        const job = recentJobs.find((j) => j.jobId === row.jobId);
        if (
          job &&
          job.state === "awaiting_processing" &&
          job.intakeChecksumStatus === "computed"
        ) {
          return { ...row, phase: "complete", progress: 100 };
        }
        if (job && (job.state === "upload_expired" || job.state === "failed")) {
          return {
            ...row,
            phase: "failed",
            error: job.reasonCode || job.state,
          };
        }
        // Previously seen on the intake list, now gone → handed off past intake.
        if (!job && seenIntakeJobIdsRef.current.has(row.jobId)) {
          return { ...row, phase: "complete", progress: 100 };
        }
        if (job && (row.phase === "processing" || row.phase === "uploading")) {
          return { ...row, phase: "processing", progress: 100 };
        }
        return row;
      }),
    );
  }, [recentJobs]);

  // Capture outcomes for the session receipt (including phases set via list sync).
  useEffect(() => {
    const tracker = batchTrackerRef.current;
    if (!tracker || tracker.finalized) return;

    const dropLocalIds = new Set<string>();

    for (const row of activeUploads) {
      if (!tracker.localIds.has(row.localId)) continue;
      if (row.jobId) {
        tracker.jobByLocalId.set(row.localId, row.jobId);
      }
      if (row.phase === "complete") {
        recordBatchOutcome(row.localId, "succeeded");
      } else if (row.phase === "failed") {
        recordBatchOutcome(row.localId, "failed");
      }
    }

    for (const [localId, jobId] of tracker.jobByLocalId) {
      if (!tracker.localIds.has(localId)) continue;
      const job = recentJobs.find((j) => j.jobId === jobId);
      if (job?.state === "awaiting_review") {
        // Job remains actionable in the intake table; drop local overlay only.
        recordBatchOutcome(localId, "needsReview");
        dropLocalIds.add(localId);
      }
    }

    if (dropLocalIds.size > 0) {
      setActiveUploads((prev) =>
        prev.filter((r) => !dropLocalIds.has(r.localId)),
      );
    }
  }, [activeUploads, recentJobs, recordBatchOutcome]);

  // Brief success flash, then drop local completed rows (durable job untouched).
  useEffect(() => {
    for (const row of activeUploads) {
      if (row.phase !== "complete") continue;
      if (completeClearScheduledRef.current.has(row.localId)) continue;
      completeClearScheduledRef.current.add(row.localId);
      window.setTimeout(() => {
        setActiveUploads((prev) =>
          prev.filter((r) => r.localId !== row.localId),
        );
        completeClearScheduledRef.current.delete(row.localId);
      }, UPLOADER_COMPLETE_CLEAR_DELAY_MS);
    }
  }, [activeUploads]);

  return (
    <Container fluid>
      <Card style={{ borderRadius: 12 }}>
        <Card.Body>
          <Card.Title style={{ display: "flex", justifyContent: "space-between" }}>
            <PageTitle title="Song Uploader" />
          </Card.Title>

          <div className={styles.contentWrapper}>
            {apiNotConfigured && (
              <Alert variant="warning">{PROCESSING_API_UNAVAILABLE_MESSAGE}</Alert>
            )}

            {formError && (
              <Alert variant="danger" onClose={() => setFormError(null)} dismissible>
                {formError}
              </Alert>
            )}

            {!canEdit && (
              <Alert variant="info">
                You have view-only access. Upload requires uploader.upload permission.
              </Alert>
            )}

            <div
              {...getRootProps()}
              className={`${styles.uploadArea} ${isDragActive ? styles.uploadAreaActive : ""} ${isDragReject ? styles.uploadAreaReject : ""} ${uploadBlockedReason ? styles.uploadAreaDisabled : ""}`}
            >
              <div className={styles.uploadTitle}>CLEAN SONGS ONLY</div>
              <img
                src={UploaderDropboxIcon}
                alt="Music Upload Icon"
                className={styles.uploadIcon}
              />
              <div className={styles.uploadDescription}>
                {canEdit
                  ? "Click or drag and drop to upload MP3 files"
                  : "Upload permission required"}
              </div>
              <div className={styles.uploadSubtext}>
                Maximum file size is {Math.floor(MUSIC_UPLOAD_MAX_BYTES / (1024 * 1024))} MB.
                MP3 only (audio/mpeg). Large batches upload a few at a time so the
                processing service is not overloaded.
              </div>
              <div className={styles.uploadNote}>
                After you select files, you will choose Song type (KK Original or
                Commercial) and Source before upload starts. Uploaded files are
                intake-only; KrossKast will process each song and generate a new
                approved playback artifact.
              </div>
              <input {...getInputProps()} />
            </div>

            {putVisualNotice.kind != null && pagePutBannerCopy && (
              <Alert
                variant={putVisualNotice.kind === "active" ? "warning" : "success"}
                className="mt-3 mb-0"
                data-testid="active-put-stay-on-page"
                data-put-visual={putVisualNotice.kind}
                data-put-episode-count={putVisualNotice.episodeFileCount}
              >
                {pagePutBannerCopy}
              </Alert>
            )}

            {sessionReceipt && (
              <div
                className={`${styles.sessionReceipt} ${
                  uploadSessionNeedsAttention(sessionReceipt)
                    ? styles.sessionReceiptAttention
                    : styles.sessionReceiptOk
                }`}
                role="status"
                aria-live="polite"
                data-testid="upload-session-receipt"
              >
                <div className={styles.sessionReceiptBody}>
                  <div className={styles.sessionReceiptHeadline}>
                    {formatUploadSessionHeadline(sessionReceipt)}
                  </div>
                  <div className={styles.sessionReceiptLead}>
                    {formatUploadSessionLead(sessionReceipt)}
                  </div>
                  <div className={styles.sessionReceiptCounts}>
                    {formatUploadSessionCounts(sessionReceipt)}
                  </div>
                  <div className={styles.sessionReceiptMeta}>
                    {formatUploadSessionCompletedAt(sessionReceipt.completedAt)}
                  </div>
                </div>
                <div className={styles.sessionReceiptActions}>
                  {uploadSessionNeedsAttention(sessionReceipt) && (
                    <button
                      type="button"
                      className={styles.sessionReceiptBtnSecondary}
                      onClick={() => {
                        intakeSectionRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }}
                    >
                      Review issues
                    </button>
                  )}
                  <Link
                    to="/processing-jobs"
                    className={styles.sessionReceiptBtnPrimary}
                  >
                    View in Song Processing
                  </Link>
                  <button
                    type="button"
                    className={styles.sessionReceiptBtnGhost}
                    onClick={() => setSessionReceipt(null)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {canView && (
              <section
                ref={intakeSectionRef}
                className={styles.recentSection}
                id="uploader-intake-queue"
              >
                <div className={styles.recentHeader}>
                  <div className={styles.recentTitleGroup}>
                    <h3 className={styles.sectionTitle}>Intake queue</h3>
                    <span
                      className={styles.processingNowCount}
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      {processingNowCount === 0
                        ? "No intake in progress"
                        : processingNowCount === 1
                          ? "1 song in intake"
                          : `${processingNowCount} songs in intake`}
                    </span>
                  </div>
                  <div className={styles.recentActions}>
                    {jobsFetching && (
                      <span className={styles.pollingHint}>
                        <LoadingOutlined /> Refreshing…
                      </span>
                    )}
                    <Link to="/processing-jobs" className={styles.viewAllLink}>
                      Follow in Song Processing
                    </Link>
                  </div>
                </div>
                <p className={styles.pollingHint}>
                  Actionable uploads only. After successful handoff, jobs leave this
                  queue — continue on Song Processing.
                  {showPollingHint
                    ? " Updates automatically while intake is in progress."
                    : ""}
                </p>

                <Table
                  bordered
                  hover
                  responsive
                  className={`legacy-row-height-table ${styles.recentJobsTable}`}
                >
                  <thead>
                    <tr>
                      <th>Filename</th>
                      <th>Song type</th>
                      <th>State</th>
                      <th>Checksum</th>
                      <th>Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mergedRecentRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className={styles.emptyCell}>
                          Intake queue is clear. Upload a song to start.
                        </td>
                      </tr>
                    ) : (
                      mergedRecentRows.map((entry) => {
                        if (entry.kind === "active") {
                          const { row, job } = entry;
                          return (
                            <tr key={row.localId}>
                              <td>{row.fileName}</td>
                              <td>{renderSongTypeBadge(row.songType)}</td>
                              <td>
                                {job
                                  ? renderStateBadge(job.state)
                                  : row.phase === "failed"
                                    ? renderStateBadge("failed")
                                    : row.phase === "queued"
                                      ? renderStateBadge("queued")
                                      : renderStateBadge("upload_initiated")}
                              </td>
                              <td>
                                {job?.intakeChecksumStatus
                                  ? renderChecksumBadge(job.intakeChecksumStatus)
                                  : "—"}
                              </td>
                              <td>{job ? formatDateTime(job.updatedAt) : "—"}</td>
                              <td>
                                {row.phase === "queued" && (
                                  <span className={styles.phaseLabel}>
                                    Waiting for upload slot…
                                  </span>
                                )}
                                {row.phase === "uploading" && (
                                  <div data-testid="active-put-progress">
                                    <ProgressBar
                                      now={row.progress}
                                      label={`${row.progress}%`}
                                      style={{ minWidth: 120 }}
                                    />
                                    <span className={styles.phaseLabel}>
                                      Uploading…
                                    </span>
                                  </div>
                                )}
                                {row.phase === "initiating" && (
                                  <span className={styles.phaseLabel}>
                                    <LoadingOutlined /> Starting…
                                  </span>
                                )}
                                {row.phase === "retrying" && (
                                  <span className={styles.phaseLabel}>
                                    <LoadingOutlined />{" "}
                                    {row.error ||
                                      formatUploadTransientMessage(
                                        row.attempt || 1,
                                        DEFAULT_UPLOAD_MAX_ATTEMPTS,
                                      )}
                                  </span>
                                )}
                                {row.phase === "processing" &&
                                  putVisualNotice.kind === "safe" && (
                                    <span
                                      className={styles.phaseLabel}
                                      data-testid="active-put-safe-row"
                                    >
                                      Upload received / Processing…
                                    </span>
                                  )}
                                {row.phase === "processing" &&
                                  !job &&
                                  putVisualNotice.kind !== "safe" && (
                                    <span className={styles.phaseLabel}>
                                      <LoadingOutlined /> Processing…
                                    </span>
                                  )}
                                {row.phase === "processing" &&
                                  job &&
                                  putVisualNotice.kind === "active" && (
                                    <span className={styles.phaseLabel}>
                                      Upload received / Processing…
                                    </span>
                                  )}
                                {row.phase === "complete" && (
                                  <span className={styles.phaseLabel}>
                                    Handed off — opening Song Processing…
                                  </span>
                                )}
                                {row.phase === "failed" && row.error && (
                                  <span className={styles.errorText}>{row.error}</span>
                                )}
                                {row.jobId && (
                                  <Link
                                    to={`/processing-jobs/${encodeURIComponent(row.jobId)}`}
                                    className={styles.detailLink}
                                  >
                                    View job
                                  </Link>
                                )}
                              </td>
                            </tr>
                          );
                        }

                        const { job } = entry;
                        return (
                          <tr key={job.jobId}>
                            <td>{job.originalFilename || "—"}</td>
                            <td>{renderSongTypeBadge(job.songType)}</td>
                            <td>{renderStateBadge(job.state)}</td>
                            <td>{renderChecksumBadge(job.intakeChecksumStatus)}</td>
                            <td>{formatDateTime(job.updatedAt)}</td>
                            <td>
                              <Link
                                to={`/processing-jobs/${encodeURIComponent(job.jobId)}`}
                                className={styles.detailLink}
                              >
                                View job
                              </Link>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
                <div className="mt-3">
                  <PaginationRow
                    page={listPage}
                    totalPages={totalPages ?? undefined}
                    onPageChange={handlePageJump}
                    onClickPrevious={handlePrev}
                    handleOnClickNext={handleNext}
                    disableLeft={listPage <= 1 || jobsFetching}
                    disableNext={jobsFetching || !jobsData?.pageToken}
                  />
                </div>
              </section>
            )}
          </div>
        </Card.Body>
      </Card>

      <UploadConfirmModal
        show={pendingFiles != null && pendingFiles.length > 0}
        fileNames={(pendingFiles || []).map((f) => f.name)}
        songType={modalSongType}
        mediaSource={modalMediaSource}
        onSongTypeChange={setModalSongType}
        onMediaSourceChange={setModalMediaSource}
        genreOptions={uploadGenreOptions}
        genreId={modalGenreId}
        subgenreId={modalSubgenreId}
        onGenreChange={setModalGenreId}
        onSubgenreChange={setModalSubgenreId}
        onCancel={resetUploadConfirmModal}
        onConfirm={handleConfirmUpload}
      />

      <DuplicateUploadModal
        show={pendingDuplicate != null}
        fileName={pendingDuplicate?.fileName ?? ""}
        details={pendingDuplicate?.details ?? null}
        onCancel={() => setPendingDuplicate(null)}
        onReplaceSongAudio={() => {
          const pending = pendingDuplicate;
          const song =
            pending?.details.replaceSongAudio?.existingSong ||
            pending?.details.existingSong ||
            null;
          if (!pending?.file || !song?.trackId) return;
          setReplaceError("");
          setReplaceTarget({
            trackId: song.trackId,
            title: song.title || pending.fileName,
            primaryArtistName: null,
            primaryArtistId: song.primaryArtistId || null,
            songType: null,
            sourceProcessingJobId: song.sourceProcessingJobId || null,
            playbackVersionNumber: song.playbackVersionNumber ?? null,
            approvedObjectKey: null,
            checksumApproved: null,
            file: pending.file,
          });
          setPendingDuplicate(null);
        }}
      />

      <ReplaceSongAudioConfirmModal
        show={replaceTarget != null}
        target={replaceTarget}
        busy={replaceBusy}
        errorMessage={replaceError}
        errorOpenPath={replaceErrorOpenPath}
        onCancel={() => {
          if (replaceBusy) return;
          setReplaceTarget(null);
          setReplaceError("");
          setReplaceErrorOpenPath(null);
        }}
        onConfirm={(replaceReason) => {
          void (async () => {
            if (!replaceTarget) return;
            setReplaceBusy(true);
            setReplaceError("");
            setReplaceErrorOpenPath(null);
            const localId = `${Date.now()}-replace-${replaceTarget.file.name}`;
            setListPage(1);
            setActiveUploads((prev) => [
              {
                localId,
                fileName: replaceTarget.file.name,
                songType: (replaceTarget.songType === "commercial"
                  ? "commercial"
                  : "kk-original") as SongType,
                phase: "initiating",
                progress: 0,
              },
              ...prev,
            ]);
            try {
              const init = await initAndPutReplaceSongAudio({
                replaceSongAudio,
                trackId: replaceTarget.trackId,
                file: replaceTarget.file,
                replaceReason,
                songType: replaceTarget.songType,
                onProgress: (pct) =>
                  setActiveUploads((prev) =>
                    prev.map((r) =>
                      r.localId === localId
                        ? { ...r, phase: "uploading", progress: pct, jobId: r.jobId }
                        : r,
                    ),
                  ),
              });
              setActiveUploads((prev) =>
                prev.map((r) =>
                  r.localId === localId
                    ? {
                        ...r,
                        jobId: init.jobId,
                        phase: "processing",
                        progress: 100,
                      }
                    : r,
                ),
              );
              setReplaceTarget(null);
            } catch (err) {
              const conflict = extractReplacementAlreadyInProgress(err);
              const message = conflict
                ? conflict.message
                : extractProcessingErrorMessage(err) || "Replace Song Audio failed";
              setReplaceError(message);
              setReplaceErrorOpenPath(conflict?.openReplacementPath ?? null);
              setActiveUploads((prev) =>
                prev.map((r) =>
                  r.localId === localId
                    ? { ...r, phase: "failed", error: message }
                    : r,
                ),
              );
            } finally {
              setReplaceBusy(false);
            }
          })();
        }}
      />
    </Container>
  );
};

export default withAuthenticationRequired(UploaderPage);
